import type { ConsentDecision } from './types.js';

const COOKIE_NAME = 'euconsent-v2';
const GPP_COOKIE = '__gpp';
const STORE_KEY = 'mpwa_cmp';
const DEFAULT_TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months — TCF requirement

export function loadDecision(): ConsentDecision | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentDecision;
    if (!parsed.expiresAt || parsed.expiresAt < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveDecision(d: Omit<ConsentDecision, 'expiresAt'>): ConsentDecision {
  const full: ConsentDecision = { ...d, expiresAt: Date.now() + DEFAULT_TTL_MS };
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, JSON.stringify(full));
  } catch {
    // localStorage might be blocked in private mode — cookie below is the fallback.
  }
  setCookie(COOKIE_NAME, full.tcString, DEFAULT_TTL_MS);
  setCookie(GPP_COOKIE, full.gppString, DEFAULT_TTL_MS);
  return full;
}

export function clearDecision(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  } catch {
    // ignore
  }
  setCookie(COOKIE_NAME, '', -1);
  setCookie(GPP_COOKIE, '', -1);
}

function setCookie(name: string, value: string, ttlMs: number): void {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + ttlMs).toUTCString();
  const host = document.location.hostname;
  // Cookie scoped to the registrable domain so it's readable across subdomains.
  const parts = host.split('.');
  const domain = parts.length > 2 ? `.${parts.slice(-2).join('.')}` : host;
  document.cookie =
    `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=${domain}; SameSite=Lax`;
}
